'use client';

import type { MysteryRoomLocalRuntime, MysteryRoomVisibilityState } from '../core/localRuntime';
import { polygonToSegments, rectToSegments } from '../world/mapTypes';
import type { MysteryRoomRect, MysteryRoomSegment, MysteryRoomVec2 } from '../world/mapTypes';

const ANGLE_EPSILON = 0.0001;
const FULL_CIRCLE_STEPS = 96;
const DEFAULT_CONE_STEPS = 44;
const staticSightBlockingSegmentsCache = new WeakMap<MysteryRoomLocalRuntime['map'], MysteryRoomSegment[]>();

export function buildVisibilityState(runtime: MysteryRoomLocalRuntime): MysteryRoomVisibilityState {
  const blockingSegments = buildSightBlockingSegments(runtime.map, runtime.doors);

  return {
    origin: runtime.hero.position,
    blockingSegments,
    radius: runtime.map.visibilityRadius,
    polygon: castVisibilityPolygon(runtime.hero.position, blockingSegments, runtime.map.visibilityRadius),
  };
}

export function buildSightBlockingSegments(
  map: MysteryRoomLocalRuntime['map'],
  doors: MysteryRoomLocalRuntime['doors'],
) {
  const staticSegments = buildStaticSightBlockingSegments(map);
  const doorSegments = doors.filter((door) => door.state !== 'open').flatMap((door) => rectToSegments(door.panel));

  if (doorSegments.length === 0) {
    return staticSegments;
  }

  return [...staticSegments, ...doorSegments];
}

export function buildStaticSightBlockingSegments(map: MysteryRoomLocalRuntime['map']) {
  const cached = staticSightBlockingSegmentsCache.get(map);
  if (cached) {
    return cached;
  }

  const segments = [
    ...map.walls,
    ...map.occluders.filter((occluder) => occluder.blocksSight).flatMap((occluder) => rectToSegments(occluder.rect)),
    ...(map.customObjects ?? [])
      .filter((object) => object.blocksSight || (object.collision.kind !== 'none' && object.collision.blocksSight))
      .flatMap((object) => {
        if (object.collision.kind === 'rect') return rectToSegments(object.collision.rect);
        if (object.collision.kind === 'polygon') return polygonToSegments(object.collision.points);
        return rectToSegments(object.rect);
      }),
  ];

  staticSightBlockingSegmentsCache.set(map, segments);
  return segments;
}

export function castVisibilityPolygon(
  origin: MysteryRoomVec2,
  segments: MysteryRoomSegment[],
  radius: number,
) {
  const angles = new Set<number>();

  for (let index = 0; index < FULL_CIRCLE_STEPS; index += 1) {
    angles.add(normalizeAngle((index / FULL_CIRCLE_STEPS) * Math.PI * 2));
  }

  for (const segment of segments) {
    for (const point of [segment.start, segment.end]) {
      const baseAngle = Math.atan2(point.y - origin.y, point.x - origin.x);
      angles.add(normalizeAngle(baseAngle - ANGLE_EPSILON));
      angles.add(normalizeAngle(baseAngle));
      angles.add(normalizeAngle(baseAngle + ANGLE_EPSILON));
    }
  }

  return Array.from(angles)
    .sort((a, b) => a - b)
    .map((angle) => castRay(origin, angle, segments, radius));
}

export function castVisibilityCone(
  origin: MysteryRoomVec2,
  segments: MysteryRoomSegment[],
  radius: number,
  facingAngle: number,
  halfAngle: number,
  steps = DEFAULT_CONE_STEPS,
) {
  const safeRadius = Math.max(1, radius);
  const safeHalfAngle = Math.max(0.01, Math.min(Math.PI, halfAngle));
  const safeSteps = Math.max(4, Math.min(96, Math.round(steps)));
  const angles = new Set<number>();
  const addConeAngle = (angle: number) => {
    if (Math.abs(angleDelta(angle, facingAngle)) <= safeHalfAngle + ANGLE_EPSILON * 8) {
      angles.add(normalizeAngle(angle));
    }
  };

  for (let index = 0; index <= safeSteps; index += 1) {
    const progress = index / safeSteps;
    addConeAngle(facingAngle - safeHalfAngle + progress * safeHalfAngle * 2);
  }

  for (const segment of segments) {
    for (const point of [segment.start, segment.end]) {
      const baseAngle = Math.atan2(point.y - origin.y, point.x - origin.x);
      addConeAngle(baseAngle - ANGLE_EPSILON);
      addConeAngle(baseAngle);
      addConeAngle(baseAngle + ANGLE_EPSILON);
    }
  }

  return Array.from(angles)
    .sort((a, b) => angleDelta(a, facingAngle) - angleDelta(b, facingAngle))
    .map((angle) => castRay(origin, angle, segments, safeRadius));
}

export function pointInPolygon(point: MysteryRoomVec2, polygon: MysteryRoomVec2[]) {
  let inside = false;

  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].x;
    const yi = polygon[i].y;
    const xj = polygon[j].x;
    const yj = polygon[j].y;

    const intersects =
      yi > point.y !== yj > point.y &&
      point.x < ((xj - xi) * (point.y - yi)) / Math.max(0.0000001, yj - yi) + xi;

    if (intersects) {
      inside = !inside;
    }
  }

  return inside;
}

export function hasUnobstructedSightLine(start: MysteryRoomVec2, end: MysteryRoomVec2, segments: MysteryRoomSegment[]) {
  return !segments.some((segment) => segmentIntersection(start, end, segment.start, segment.end));
}

export function isPointVisible(
  visibility: MysteryRoomVisibilityState | null | undefined,
  point: MysteryRoomVec2,
  options: { requireLineOfSight?: boolean } = {},
) {
  if (!visibility) {
    return true;
  }

  if (options.requireLineOfSight === false) {
    return pointInPolygon(point, visibility.polygon) || isInsideVisibilityRadius(visibility, point);
  }

  if (!pointInPolygon(point, visibility.polygon)) {
    return isInsideVisibilityRadius(visibility, point) && hasUnobstructedSightLine(visibility.origin, point, visibility.blockingSegments);
  }

  return hasUnobstructedSightLine(visibility.origin, point, visibility.blockingSegments);
}

function isInsideVisibilityRadius(visibility: MysteryRoomVisibilityState, point: MysteryRoomVec2) {
  if (typeof visibility.radius !== 'number') {
    return false;
  }

  const dx = point.x - visibility.origin.x;
  const dy = point.y - visibility.origin.y;
  return dx * dx + dy * dy <= (visibility.radius + 1) ** 2;
}

export function isCircleVisible(
  visibility: MysteryRoomVisibilityState | null | undefined,
  center: MysteryRoomVec2,
  radius: number,
  options: { requireLineOfSight?: boolean } = {},
) {
  if (!visibility) {
    return true;
  }

  return circleSamplePoints(center, radius).some((point) => isPointVisible(visibility, point, options));
}

export function isRectVisible(
  visibility: MysteryRoomVisibilityState | null | undefined,
  rect: MysteryRoomRect,
  options: { requireLineOfSight?: boolean } = {},
) {
  if (!visibility) {
    return true;
  }

  return rectSamplePoints(rect).some((point) => isPointVisible(visibility, point, options));
}

function castRay(
  origin: MysteryRoomVec2,
  angle: number,
  segments: MysteryRoomSegment[],
  radius: number,
) {
  const end = rayEnd(origin, angle, radius);
  let closest = end;
  let closestDistanceSq = squaredDistance(origin, end);

  for (const segment of segments) {
    const hit = raySegmentIntersection(origin, end, segment.start, segment.end);
    if (!hit) {
      continue;
    }

    const distanceSq = squaredDistance(origin, hit);
    if (distanceSq < closestDistanceSq) {
      closest = hit;
      closestDistanceSq = distanceSq;
    }
  }

  return closest;
}

function raySegmentIntersection(
  rayStart: MysteryRoomVec2,
  rayEnd: MysteryRoomVec2,
  segStart: MysteryRoomVec2,
  segEnd: MysteryRoomVec2,
) {
  const r = {
    x: rayEnd.x - rayStart.x,
    y: rayEnd.y - rayStart.y,
  };
  const s = {
    x: segEnd.x - segStart.x,
    y: segEnd.y - segStart.y,
  };
  const denominator = cross(r, s);

  if (Math.abs(denominator) < 0.0000001) {
    return null;
  }

  const qp = {
    x: segStart.x - rayStart.x,
    y: segStart.y - rayStart.y,
  };
  const t = cross(qp, s) / denominator;
  const u = cross(qp, r) / denominator;

  if (t < 0 || t > 1 || u < 0 || u > 1) {
    return null;
  }

  return {
    x: rayStart.x + t * r.x,
    y: rayStart.y + t * r.y,
  };
}

function segmentIntersection(
  firstStart: MysteryRoomVec2,
  firstEnd: MysteryRoomVec2,
  secondStart: MysteryRoomVec2,
  secondEnd: MysteryRoomVec2,
) {
  const a = {
    x: firstEnd.x - firstStart.x,
    y: firstEnd.y - firstStart.y,
  };
  const b = {
    x: secondEnd.x - secondStart.x,
    y: secondEnd.y - secondStart.y,
  };
  const denominator = cross(a, b);

  if (Math.abs(denominator) < 0.0000001) {
    return false;
  }

  const offset = {
    x: secondStart.x - firstStart.x,
    y: secondStart.y - firstStart.y,
  };
  const t = cross(offset, b) / denominator;
  const u = cross(offset, a) / denominator;

  return t >= 0 && t <= 1 && u >= 0 && u <= 1;
}

function rayEnd(origin: MysteryRoomVec2, angle: number, radius: number) {
  return {
    x: origin.x + Math.cos(angle) * radius,
    y: origin.y + Math.sin(angle) * radius,
  };
}

function normalizeAngle(angle: number) {
  let normalized = angle;
  while (normalized < -Math.PI) normalized += Math.PI * 2;
  while (normalized >= Math.PI) normalized -= Math.PI * 2;
  return normalized;
}

function angleDelta(angle: number, center: number) {
  return normalizeAngle(angle - center);
}

function circleSamplePoints(center: MysteryRoomVec2, radius: number) {
  const sampleRadius = Math.max(0, radius);
  if (sampleRadius === 0) {
    return [center];
  }

  const diagonal = sampleRadius * 0.70710678118;
  return [
    center,
    { x: center.x + sampleRadius, y: center.y },
    { x: center.x - sampleRadius, y: center.y },
    { x: center.x, y: center.y + sampleRadius },
    { x: center.x, y: center.y - sampleRadius },
    { x: center.x + diagonal, y: center.y + diagonal },
    { x: center.x - diagonal, y: center.y + diagonal },
    { x: center.x + diagonal, y: center.y - diagonal },
    { x: center.x - diagonal, y: center.y - diagonal },
  ];
}

function rectSamplePoints(rect: MysteryRoomRect) {
  const center = {
    x: rect.x + rect.width / 2,
    y: rect.y + rect.height / 2,
  };
  return [
    center,
    { x: rect.x, y: rect.y },
    { x: rect.x + rect.width, y: rect.y },
    { x: rect.x, y: rect.y + rect.height },
    { x: rect.x + rect.width, y: rect.y + rect.height },
  ];
}

function squaredDistance(a: MysteryRoomVec2, b: MysteryRoomVec2) {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return dx * dx + dy * dy;
}

function cross(a: MysteryRoomVec2, b: MysteryRoomVec2) {
  return a.x * b.y - a.y * b.x;
}
